import React, { useCallback, useState } from 'react';
import ReactModal from 'react-modal';
import { useToasts } from 'react-toast-notifications';
import Toggle from '../common/Toggle';
import { PricingPage } from '../types/pricingPage';
import { useAxios } from '../utils/hooks';
import Alert from './Alert';
import Modal, { MODAL_STYLES } from './Modal';

const TOGGLE_TEXT = { checked: 'Live', unchecked: 'Test' };

interface Props {
    page: PricingPage;
    onChangeStatus?: (status: PricingPage['status']) => void;
    liveWarning?: boolean;
    disabled?: boolean;
    treatDraftAsTest?: boolean;
}

export default function PageStatus({
    page,
    treatDraftAsTest = false,
    liveWarning = true,
    disabled = false,
    onChangeStatus,
}: Props) {
    const { addToast } = useToasts();
    const axios = useAxios();

    const [modalOpen, setModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [checkBoxState, setCheckBoxState] = useState(page.status === 'ACTIVE');
    const handleCancelModal = () => {
        setCheckBoxState(!checkBoxState);
        setModalOpen(false);
    };

    const handleChangeStatus = useCallback(
        (live: boolean) => {
            setIsSaving(true);
            const newStatus = live ? 'ACTIVE' : 'TEST';
            axios.current
                ?.post(`/pricing-pages/${page.id}/status`, {
                    status: newStatus,
                } as PricingPage)
                .then(() => {
                    setModalOpen(false);
                    onChangeStatus?.(newStatus);
                })
                .catch((err) => {
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                    setCheckBoxState(!live);
                    setModalOpen(false);
                })
                .finally(() => {
                    setIsSaving(false);
                });
        },
        [addToast, axios, onChangeStatus, page.id]
    );
    const handleCheckboxChange = (live: boolean) => {
        setCheckBoxState(live);
        if (live && liveWarning) {
            setModalOpen(true);
        } else if (onChangeStatus) {
            onChangeStatus(live ? 'ACTIVE' : 'TEST');
        } else {
            handleChangeStatus(live);
        }
    };

    if (page.status === 'DRAFT' && !treatDraftAsTest) {
        return <Alert title="Draft" colour="GREY" />;
    }
    return (
        <>
            <Toggle
                name={`${page.id}-mode`}
                checked={checkBoxState}
                onChange={handleCheckboxChange}
                disabledColour="YELLOW"
                text={TOGGLE_TEXT}
                disabled={disabled}
            />
            <ReactModal isOpen={modalOpen} style={MODAL_STYLES}>
                <Modal
                    type="INFO"
                    title={`Activate Live Mode for ${page.name}?`}
                    body={
                        <>
                            This will activate live mode for your pricing page immediately. That means it will start
                            accepting real credit card details and actual payments.{' '}
                            {page.plans.length > 0 && (
                                <>
                                    If you have the same products set up in Stripe, they will be used. If not, we will
                                    automatically create a live copy of your chosen products in Stripe.
                                </>
                            )}
                        </>
                    }
                    confirmButtonText="Go Live"
                    onConfirm={() => handleChangeStatus(true)}
                    onCancel={handleCancelModal}
                    state={isSaving ? 'SAVING' : 'READY'}
                />
            </ReactModal>
        </>
    );
}
